// Export.js
import React, { useEffect, createContext, useContext, useState } from 'react';
import { useGetAuthLink, useTriggerGetStatus, useTriggerExport } from '~/requests';
import { useToastMessage, ToastVariant } from '~/Hooks';
import { useTranslation } from 'react-i18next';

// Define the context type
interface ExportContextProps {
  failureMessage?: string;
  status: string;
  startExport: (obj?: any) => void;
  data: any;
  link: string;
}

// Create the context with the defined type
const ExportContext = createContext<ExportContextProps | null>(null);

// Create a provider
interface ExportProviderProps {
  children: React.ReactNode;
}

export const ExportProvider = ({ children }: ExportProviderProps) => {
  const { t } = useTranslation();
  const { showToast } = useToastMessage();
  const [link, setLink] = useState(null as any); // unstructured
  const { mutate: startExporting, data } = useTriggerExport(); // unstructured
  const { refetch: getStatus, data: statusResponse } = useTriggerGetStatus(data); // unstructured
  const { refetch: getExportAuthLink, data: authLinkResponse } = useGetAuthLink();

  useEffect(() => {
    if (!!data?.length) {
      getStatus(data);
    }
  }, [data?.length]);

  useEffect(() => {
    if (authLinkResponse == undefined) {
      return;
    }
    setLink(authLinkResponse?.link);
    const visitLink = authLinkResponse?.link;
    if (!!visitLink?.length) {
      const content = (
        <div>
          <a
            href={visitLink}
            target="_blank"
            rel="noopener noreferrer"
            onClick={() => window.open(visitLink, '_blank')}
          >
            {`${t('neo.export.link.open')}: `}
          </a>
        </div>
      );
      showToast('neo.export.link.open', content, ToastVariant.SUCCESS, false, undefined, 15000);
    }
  }, [authLinkResponse]);

  useEffect(() => {
    if (statusResponse?.status === 'Success') {
      getExportAuthLink();
    }
  }, [statusResponse?.status]);

  const value = {
    status: statusResponse?.status || '',
    failureMessage: statusResponse?.failureMessage,
    data,
    link,
    startExport: startExporting,
  };

  return <ExportContext.Provider value={value}>{children}</ExportContext.Provider>;
};

// Custom hook to use the shared state
export const useExport = () => {
  const context = useContext(ExportContext);
  return { ...context } as any;
};
